/**
 * React Query hooks for Catalog operations
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CatalogListParams, catalogsApi, getCatalogId } from '@/lib/api/catalogs';
import { Catalog, CatalogFormData, CatalogListResponse } from '../types/catalog';

// Query keys factory for consistent cache management
export const catalogKeys = {
  all: ['catalogs'] as const,
  lists: () => [...catalogKeys.all, 'list'] as const,
  list: (params?: CatalogListParams) => [...catalogKeys.lists(), params] as const,
  details: () => [...catalogKeys.all, 'detail'] as const,
  detail: (id: string) => [...catalogKeys.details(), id] as const,
};

/**
 * Hook to fetch list of catalogs
 */
export function useCatalogs(params?: CatalogListParams) {
  return useQuery({
    queryKey: catalogKeys.list(params),
    queryFn: async () => {
      const response = await catalogsApi.list(params);

      // Handle multiple response formats:
      // 1. { data: { items: [...], total } } - wrapped format
      // 2. { items: [...], total } - direct format
      // 3. [...] - array format
      const responseWithData = response as CatalogListResponse & {
        data?: CatalogListResponse;
      };
      const data = responseWithData.data || response;
      const items = data.items || (data as unknown as Catalog[]);

      // Ensure we have an array
      if (!Array.isArray(items)) {
        console.error('Invalid catalog response format:', response);
        return { items: [], total: 0 };
      }

      // Ensure each catalog has an id field for UI compatibility
      const catalogsWithId = items.map((catalog) => ({
        ...catalog,
        id: getCatalogId(catalog.name),
      }));

      // Return in expected format
      return {
        items: catalogsWithId,
        total: data.total || catalogsWithId.length,
      };
    },
  });
}

/**
 * Hook to fetch a single catalog by name
 */
export function useCatalog(name: string, options?: { enabled?: boolean }) {
  const id = getCatalogId(name);
  return useQuery({
    queryKey: catalogKeys.detail(id),
    queryFn: () => catalogsApi.get(name),
    enabled: options?.enabled ?? !!name,
  });
}

/**
 * Hook to create a new catalog
 */
export function useCreateCatalog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CatalogFormData) => catalogsApi.create(data),
    onSuccess: () => {
      // Invalidate all catalog lists to refetch
      queryClient.invalidateQueries({ queryKey: catalogKeys.lists() });
    },
  });
}

/**
 * Hook to update an existing catalog
 */
export function useUpdateCatalog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, data }: { name: string; data: Partial<CatalogFormData> }) => {
      return catalogsApi.update(name, data);
    },
    onSuccess: (_updatedCatalog, { name }) => {
      const id = getCatalogId(name);
      // Invalidate so the next observer refetches the full catalog. The PUT
      // response is a CatalogCreateResponse stub ({message, name, namespace})
      // — writing it straight into the detail cache with setQueryData would
      // poison subsequent reads until the next refetch resolves.
      queryClient.invalidateQueries({ queryKey: catalogKeys.detail(id) });
      // Invalidate lists to reflect changes
      queryClient.invalidateQueries({ queryKey: catalogKeys.lists() });
    },
  });
}

/**
 * Hook to replace a catalog's schema filtering.
 *
 * Distinct from useUpdateCatalog: it targets the filters route, which is gated
 * on catalogs:schema_update, so holding it does not imply the ability to edit
 * the catalog's connection details.
 */
export function useUpdateCatalogFilters() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, schemas }: { name: string; schemas: string[] }) => {
      return catalogsApi.updateFilters(name, { schemas });
    },
    onSuccess: (_result, { name }) => {
      const id = getCatalogId(name);
      // Same reasoning as useUpdateCatalog: the PUT returns a stub, not the
      // catalog, so invalidate rather than writing it into the detail cache.
      queryClient.invalidateQueries({ queryKey: catalogKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: catalogKeys.lists() });
    },
  });
}

/**
 * Hook to delete a catalog
 */
export function useDeleteCatalog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name }: { name: string }) => {
      return catalogsApi.delete(name);
    },
    onSuccess: (_, { name }) => {
      const id = getCatalogId(name);
      // Remove from cache
      queryClient.removeQueries({ queryKey: catalogKeys.detail(id) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: catalogKeys.lists() });
    },
  });
}

/**
 * Hook to sync/refresh a catalog
 */
export function useSyncCatalog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      name,
      params,
    }: {
      name: string;
      params: import('@/features/catalogs/types/catalog').CatalogRefreshRequest;
    }) => {
      return catalogsApi.refresh(name, params);
    },
    onSuccess: (_, { name }) => {
      const id = getCatalogId(name);
      // Invalidate the specific catalog to refetch with updated status
      queryClient.invalidateQueries({ queryKey: catalogKeys.detail(id) });
      // Invalidate lists to reflect status change
      queryClient.invalidateQueries({ queryKey: catalogKeys.lists() });
    },
  });
}

/**
 * Hook to test catalog connection (for create mode)
 */
export function useTestCatalogConnection() {
  return useMutation({
    mutationFn: (data: CatalogFormData) => catalogsApi.testConnection(data),
  });
}

/**
 * Hook to fetch schemas by catalog name (for edit mode)
 * Uses the catalog's existing configuration stored on the server
 */
export function useCatalogSchemasByName(catalogName: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['catalog-schemas-by-name', catalogName],
    queryFn: () => catalogsApi.getSchemasByName(catalogName),
    enabled: options?.enabled ?? !!catalogName,
    staleTime: 30000, // Cache for 30 seconds
    retry: 1, // Only retry once on failure
  });
}

/**
 * Hook to fetch schemas for a catalog from storage service
 */
export function useCatalogSchemas(catalogName: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['catalog-schemas', catalogName],
    queryFn: () => {
      // For now, use a default user name. In production, this should come from auth context
      // or be passed as a parameter
      const userName = 'default-user';
      return catalogsApi.getSchemas(catalogName, userName);
    },
    enabled: options?.enabled ?? !!catalogName,
  });
}
